<i18n locale="en" lang="json">
{
  "intro": "If you have any questions or special requests, please do not hesitate to contact our customer service.",
  "team": "team name",
  "phone": "Phone",
  "email": "Email address"
}
</i18n>
<i18n locale="nl" lang="json">
{
  "intro": "Heeft u een vraag of een speciaal verzoek: aarzel dan niet om met onze klantenservice contact op te nemen.",
  "team": "Teamnaam",
  "phone": "Telefoon",
  "email": "E-mailadres"
}
</i18n>

<template>
  <div class="mb-4">{{ t('intro') }}</div>
  <base-content-list
    :loading="loadingInvoiceContactData || loadingContactData"
    :items="[
      teamNaam ? { label: t('team'), value: teamNaam } : undefined,
      { label: t('phone'), value: telephoneNumber },
      {
        id: 'email',
        label: t('email'),
        href: href,
        value: email,
      },
    ]"
    test-id="contactData"
  />
</template>

<script lang="ts" setup>
import { gql } from '@apollo/client/core';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import zvdzUseQuery from '@/common/composition/zvdzUseQuery';
import BaseContentList from '@/common/components/zvdzo/BaseContentList.vue';
import type { ZvdZID } from '@/types';
import useContactData from '@/common/composition/useContactData';

type ContactData = {
  naam: string;
  clientCode: string;
  dynTeamNaam: string;
  dynTelefoon: string;
  dynEmail: string;
  mailOnderwerp: string;
};

const GET_CONTACT_DATA = gql`
  query dienstverbandContactQuery($path: string) {
    dienstverbandContact(path: $path)
      @rest(method: "GET", path: "{args.path}", type: "ContactData") {
      naam
      clientCode
      dynTeamNaam
      dynTelefoon
      dynEmail
      mailOnderwerp
    }
  }
`;

const props = defineProps<{ dienstverbandId: ZvdZID | undefined }>();
const { t } = useI18n();

const hasDienstverbandId = computed(() => Boolean(props.dienstverbandId));
const { loading: loadingInvoiceContactData, invoiceContactData } =
  useContactData(hasDienstverbandId);

const path = computed<string>(
  () => `Contact?jsonOnly=true&dienstverbandId=${props.dienstverbandId}`,
);

const { singleResult: contactData, loading: loadingContactData } =
  zvdzUseQuery<ContactData>(
    GET_CONTACT_DATA,
    () => ({
      path: path.value,
    }),
    { enabled: Boolean(props.dienstverbandId) },
  );

const telephoneNumber = computed(() =>
  hasDienstverbandId.value
    ? contactData.value?.dynTelefoon
    : invoiceContactData.value?.telefoonnummer,
);
const teamNaam = computed(() =>
  hasDienstverbandId.value ? contactData.value?.dynTeamNaam : undefined,
);
const email = computed(() =>
  hasDienstverbandId.value
    ? contactData.value?.dynEmail
    : invoiceContactData.value?.email,
);
const href = computed(() =>
  hasDienstverbandId.value
    ? `mailto:${email.value}?Subject=${encodeURIComponent(contactData.value?.mailOnderwerp as string)}`
    : `mailto:${email.value}`,
);
</script>
